import React, { useState, useRef, useContext } from "react";
import * as XLSX from "xlsx";
import "./BulkUploadModal.css";
import "./ResponsiveModal.css";
import { NotificationsContext } from "../context/NotificationsContext";
import getApiUrl from "../utils/api";

const BulkUploadYesNoModal = ({ open, onClose, onBulkUploadSuccess }) => {
  const { addNotification } = useContext(NotificationsContext);
  const [file, setFile] = useState(null);
  const [previewData, setPreviewData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isDownloading, setIsDownloading] = useState(false);
  const fileInputRef = useRef(null);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;

    // Validate file type - only XLSX
    const validTypes = [
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    ];

    if (!validTypes.includes(selectedFile.type)) {
      setError("Please upload a valid Excel file (.xlsx)");
      return;
    }

    setFile(selectedFile);
    setError("");
    setSuccess("");
    setPreviewData([]);

    // Read and preview the file
    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const data = new Uint8Array(event.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

        // Convert to our expected format
        const processedData = processExcelData(jsonData);
        setPreviewData(processedData);
      } catch (err) {
        setError("Error reading file. Please check the file format.");
        console.error(err);
      }
    };
    reader.readAsArrayBuffer(selectedFile);
  };

  const processExcelData = (jsonData) => {
    if (jsonData.length < 2) {
      setError("File must contain at least a header row and one data row");
      return [];
    }

    const headers = jsonData[0];
    const dataRows = jsonData.slice(1);

    return dataRows.map((row, index) => {
      const item = {};
      headers.forEach((header, colIndex) => {
        const value = row[colIndex] || "";
        const cleanHeader = header
          ? header.toString().trim().toLowerCase()
          : "";
        // Normalize headers: remove parenthetical hints and unify separators
        const headerKey = cleanHeader
          .replace(/\(.*?\)/g, "") // remove anything in parentheses
          .replace(/\s+/g, " ")
          .replace(/[\s-]+/g, "_") // convert spaces and hyphens to underscores
          .trim();

        switch (headerKey) {
          case "name":
          case "task_name":
          case "title":
            item.name = value;
            break;
          case "type":
          case "task_type":
            item.type = value;
            break;
          case "location":
          case "dept":
          case "department":
            item.location = value;
            break;
          case "kra":
          case "kpi":
            item.kra = value;
            break;
          case "description":
          case "desc":
          case "remarks":
            item.description = value;
            break;
          case "budget_qty":
          case "budgetqty":
          case "qty":
            item.budgetQty = value;
            break;
          case "frequency":
          case "freq":
            item.frequency = value;
            break;
          case "occurrence_date":
          case "occurrencedate":
          case "date":
          case "start_date":
            item.occurrenceDate = formatDateForBackend(value);
            break;
          case "recurrence_group_id":
          case "recurrencegroupid":
          case "group_id":
            item.recurrenceGroupId = value;
            break;
          default:
            break;
        }
      });

      return {
        ...item,
        rowIndex: index + 2, // Excel row number (1-based, +1 for header)
        isValid: validateItem(item),
      };
    });
  };

  const formatDateForBackend = (value) => {
    if (!value) return "";

    // If it's already a string in proper format
    if (typeof value === "string") {
      // Try YYYY-MM-DD format
      if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
        return value;
      }
      // Try DD-MM-YYYY format
      if (/^\d{2}-\d{2}-\d{4}$/.test(value)) {
        const parts = value.split("-");
        return `${parts[2]}-${parts[1]}-${parts[0]}`;
      }
      return value;
    }

    // Excel date serial: days since 1899-12-30
    if (typeof value === "number") {
      const millis = (value - 25569) * 86400 * 1000;
      const date = new Date(millis);
      if (isNaN(date.getTime())) return "";
      const yyyy = date.getFullYear();
      const mm = String(date.getMonth() + 1).padStart(2, "0");
      const dd = String(date.getDate()).padStart(2, "0");
      return `${yyyy}-${mm}-${dd}`;
    }

    return "";
  };

  const validateItem = (item) => {
    // Required fields: name, type, location, frequency
    return (
      item.name &&
      item.type &&
      item.location &&
      item.frequency &&
      ["Daily", "Alternate", "Weekly", "Monthly", "Quarterly", "Yearly", "Once", "Custom"].includes(
        item.frequency
      )
    );
  };

  const handleUpload = async () => {
    if (!file || previewData.length === 0) {
      setError("Please select a file and ensure it contains valid data");
      return;
    }

    const validItems = previewData.filter((item) => item.isValid);
    if (validItems.length === 0) {
      setError(
        "No valid items found. Please check: name, type, location, and frequency (Daily/Alternate/Weekly/Monthly/Quarterly/Yearly/Once/Custom)"
      );
      return;
    }

    setLoading(true);
    setError("");
    setSuccess("");
    setUploadProgress(0);

    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await fetch(getApiUrl("api/yesno/bulk/upload"), {
        method: "POST",
        body: formData,
        credentials: "include",
      });

      let result;
      const responseContentType = response.headers.get("content-type") || "";

      if (responseContentType.includes("application/json")) {
        result = await response.json();
      } else {
        const text = await response.text();
        result = text ? { message: text } : {};
      }

      if (response.ok) {
        if (result.createdCount > 0) {
          const successMsg = `✅ Successfully created ${result.createdCount} Daily Checklist item(s)${result.errorCount > 0
            ? ` (⚠️ ${result.errorCount} error(s) occurred)`
            : ""
            }`;
          setSuccess(successMsg);
          setUploadProgress(100);

          // Fire notification
          try {
            addNotification({
              type: "yesno_created",
              title: "Bulk upload complete",
              message: `${result.createdCount} Daily Checklist item(s) created${result.errorCount > 0
                ? `, ${result.errorCount} error(s)`
                : ""
                }.`,
              meta: { created: result.createdCount, errors: result.errorCount },
            });
          } catch (notifErr) {
            console.error("Notification error:", notifErr);
          }

          // Close modal after a delay
          setTimeout(() => {
            if (onBulkUploadSuccess) onBulkUploadSuccess();
            handleClose();
          }, 3000);
        } else {
          setError(
            `❌ ${result.message || "No items were created. Please check your data."}`
          );
          if (result.errors && result.errors.length > 0) {
            const errorDetails = result.errors
              .slice(0, 3)
              .map((e) => `Row ${e.row}: ${e.error}`)
              .join("\n");
            setError((prev) => `${prev}\n\nDetails:\n${errorDetails}`);
          }
        }
      } else {
        const errorMsg =
          result.message || result.error || "Upload failed";
        setError(
          `❌ ${errorMsg}`
        );
      }
    } catch (err) {
      const fallbackMessage =
        err?.message && !/NetworkError/i.test(err.message)
          ? err.message
          : "Network error. Please try again.";
      setError(`❌ ${fallbackMessage}`);
      console.error("Upload error:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFile(null);
    setPreviewData([]);
    setError("");
    setSuccess("");
    setUploadProgress(0);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    onClose();
  };

  const downloadTemplate = () => {
    const template = [
      [
        "name",
        "type",
        "location",
        "department",
        "frequency",
        "kra",
        "description",
        "budgetQty",
        "occurrenceDate (YYYY-MM-DD)",
        "customDays (if frequency=Custom)",
      ],
      [
        "Daily Store Check",
        "Checklist",
        "Head Office",
        "Operations",
        "Daily",
        "Store Ops",
        "Check opening status",
        "1",
        "2024-10-27",
        "",
      ],
      [
        "Weekly Safety Audit",
        "Audit",
        "Warehouse",
        "Logistics",
        "Weekly",
        "Safety",
        "Weekly safety check",
        "1",
        "2024-10-27",
        "",
      ],
      [
        "Custom Days Check",
        "Checklist",
        "Store",
        "Sales",
        "Custom",
        "Sales Ops",
        "Mon-Wed-Fri Check",
        "1",
        "2024-10-27",
        "Mon,Wed,Fri",
      ],
    ];

    const ws = XLSX.utils.aoa_to_sheet(template);
    ws["!cols"] = [
      { wch: 25 }, // name
      { wch: 15 }, // type
      { wch: 15 }, // location
      { wch: 15 }, // department
      { wch: 15 }, // frequency
      { wch: 15 }, // kra
      { wch: 30 }, // description
      { wch: 12 }, // budgetQty
      { wch: 25 }, // occurrenceDate
      { wch: 25 }, // customDays
    ];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "YesNo Template");

    XLSX.writeFile(wb, "YesNo_Bulk_Upload_Template.xlsx");
  };

  const handleDownloadValidValues = async () => {
    setIsDownloading(true);
    try {
      const response = await fetch(getApiUrl("api/yesno/distinct-values"), {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}` // Ensure auth if needed, though credentials: include might handle cookie
        },
        credentials: "include",
      });

      if (!response.ok) {
        throw new Error("Failed to fetch valid values");
      }

      const result = await response.json();
      if (!result.success) {
        throw new Error(result.message || "Failed to fetch valid values");
      }

      const { departments, locations, frequencies } = result.data;

      // Create a workbook
      const wb = XLSX.utils.book_new();

      // Helper to create sheet from array of strings
      const createSheetFromValues = (title, values) => {
        const data = values.map(v => [v]);
        const ws = XLSX.utils.aoa_to_sheet([[title], ...data]);
        ws['!cols'] = [{ wch: 30 }];
        return ws;
      };

      // Create a single sheet with columns for easy reference? 
      // User said: "provide the Distinct values of the headers of the keys of location department and frequency"
      // Let's create one sheet with 3 columns.

      const maxLength = Math.max(departments.length, locations.length, frequencies.length);
      const combinedData = [];

      // Header row
      combinedData.push(["Existing Departments", "Existing Locations", "Used Frequencies"]);

      for (let i = 0; i < maxLength; i++) {
        combinedData.push([
          departments[i] || "",
          locations[i] || "",
          frequencies[i] || ""
        ]);
      }

      const ws = XLSX.utils.aoa_to_sheet(combinedData);
      ws['!cols'] = [{ wch: 30 }, { wch: 30 }, { wch: 20 }];

      XLSX.utils.book_append_sheet(wb, ws, "Valid Values");
      XLSX.writeFile(wb, "Valid_Values_Reference.xlsx");

    } catch (err) {
      console.error("Error downloading valid values:", err);
      setError("Failed to download valid values. Please try again.");
    } finally {
      setIsDownloading(false);
    }
  };

  if (!open) return null;

  return (
    <div className="bulk-upload-overlay modal-overlay-responsive">
      <div className="bulk-upload-modal modal-content-responsive">
        <div className="bulk-upload-header modal-header-responsive">
          <h3 className="modal-title-responsive">Bulk Upload Yes/No Items</h3>
          <button
            className="bulk-upload-close modal-close-responsive"
            onClick={handleClose}
          >
            ×
          </button>
        </div>

        <div className="bulk-upload-content modal-body-responsive modal-scrollable-content">
          <div className="upload-section">
            <div className="file-upload-area">
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx"
                onChange={handleFileChange}
                className="file-input"
              />
              <div className="upload-placeholder">
                <div className="upload-icon">📁</div>
                <p>Click to upload Excel file or drag and drop</p>
                <p className="upload-hint">Supports .xlsx files only</p>
              </div>
            </div>

            <div className="template-section">
              <button className="template-btn" onClick={downloadTemplate}>
                📥 Download Template
              </button>
              <button
                className="template-btn"
                onClick={handleDownloadValidValues}
                disabled={isDownloading}
                style={{ marginLeft: '10px', background: isDownloading ? '#93c5fd' : '#3b82f6', cursor: isDownloading ? 'not-allowed' : 'pointer' }}
              >
                {isDownloading ? '⏳ Downloading...' : '📋 Download Valid Values'}
              </button>
              <p className="template-hint">
                Use our template to ensure correct format
              </p>
            </div>

            <div className="instructions-section">
              <h4>Instructions:</h4>
              <ul>
                <li><strong>Required Fields:</strong> Name, Type, Location, Frequency</li>
                <li><strong>Frequency Values:</strong> Daily, Alternate, Weekly, Monthly, Quarterly, Yearly, Once, Custom</li>
                <li><strong>Date Format:</strong> YYYY-MM-DD (e.g., 2025-01-15)</li>
                <li><strong>Optional Fields:</strong> KRA, Description, Budget_Qty, Occurrence_Date, Recurrence_Group_ID</li>
              </ul>
            </div>
          </div>

          {error && (
            <div className="bulk-upload-error">
              {error.split("\n").map((line, idx) => (
                <div key={idx}>{line}</div>
              ))}
            </div>
          )}
          {success && <div className="bulk-upload-success">{success}</div>}

          {previewData.length > 0 && (
            <div className="preview-section">
              <h4>Preview ({previewData.length} items)</h4>
              <div className="preview-table-container">
                <table className="preview-table">
                  <thead>
                    <tr>
                      <th>Row</th>
                      <th>Name</th>
                      <th>Type</th>
                      <th>Location</th>
                      <th>Frequency</th>
                      <th>Valid</th>
                    </tr>
                  </thead>
                  <tbody>
                    {previewData.map((item, index) => (
                      <tr
                        key={index}
                        className={item.isValid ? "valid-row" : "invalid-row"}
                      >
                        <td>{item.rowIndex}</td>
                        <td>{item.name || "Missing"}</td>
                        <td>{item.type || "Missing"}</td>
                        <td>{item.location || "Missing"}</td>
                        <td>{item.frequency || "Missing"}</td>
                        <td>
                          <span
                            className={`validity-badge ${item.isValid ? "valid" : "invalid"
                              }`}
                          >
                            {item.isValid ? "✓" : "✗"}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {loading && (
            <div className="upload-progress">
              <div className="progress-bar">
                <div
                  className="progress-fill"
                  style={{ width: `${uploadProgress}%` }}
                ></div>
              </div>
              <p>Uploading Yes/No items... {uploadProgress}%</p>
            </div>
          )}
        </div>

        <div className="bulk-upload-actions modal-footer-responsive">
          <button className="bulk-upload-cancel" onClick={handleClose}>
            Cancel
          </button>
          <button
            className="bulk-upload-submit"
            onClick={handleUpload}
            disabled={loading || previewData.length === 0}
          >
            {loading ? "Uploading..." : "Upload Items"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BulkUploadYesNoModal;
